// @ts-nocheck
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { useSnackbar } from 'notistack';
import { encrypt } from '../utils/cryptoUtils';
import keycloak from '../keycloak-config';
import {
  selectAuth,
  setAuth,
  setPanelNavigation,
} from '../state/slices/appSlice';

import { API_ENDPOINTS } from '../const/ApiEndpoints';
import { persistor } from '../state/store/Store';
import { resetCommonState } from '../state/slices/commonSlice';

/**
 * @function useAuth
 * @description a custom hook which handles APIs related to user's authentication
 * @returns {object} object
 */
const useAuth = () => {
  const { isLoggedIn, user, panelNavigation, org } = useSelector(selectAuth);
  const [userdetails, setUserdetails] = useState({});
  const [refreshTokenIntervalObj, setRefreshTokenIntervalObj] = useState();
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [email, setEmail] = useState(null);
  const [panelData, setPanelData] = useState();
  const [isLoading, setIsLoading] = React.useState(true);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  /**
   * @function login
   * @description A function to post user's data for authentication
   * @returns {Promise} returns an axios promise
   */
  const login = (data, mode = null) => {
    if (mode === 'otp') {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    return axios
      .post(
        API_ENDPOINTS.LOGIN,
        {
          ...data,
          password: encrypt(data.password),
        },
        {
          headers: {
            org: data.accountId,
          },
        }
      )
      .then(
        (res) => {
          setIsLoading(false);
          setUserdetails({ ...res?.data, org: data.accountId });
          enqueueSnackbar('OTP sent successfully', {
            variant: 'success',
          });
          if (res?.data?.password_expiry_warning) {
            setPasswordMessage(res?.data?.password_expiry_warning);
          }
        },
        (err) => {
          setIsLoading(true);
          if (err?.response?.data?.password_expired) {
            setPasswordError(true);
            setEmail(err?.response?.data?.email);
          }
        }
      );
  };
  const apiLogout = () => {
    setIsLoading(true);
    return axios.post(API_ENDPOINTS.LOGOUT).then(
      () => {
        setIsLoading(false);
        enqueueSnackbar('System logged out successfully', {
          variant: 'success',
        });
        return Promise.resolve();
      },
      (error) => {
        setIsLoading(false);
        throw error;
      }
    );
  };
  /**
   * @function loginOtp
   * @description A function to post otp
   * @returns {Promise} returns an axios promise
   */

  const loginOtp = (data, history) => {
    const body = {
      username: userdetails?.username,
      email: userdetails?.email,
      otp: data.otp,
      password: encrypt(data.password),
    };
    return axios
      .post(API_ENDPOINTS.OTP, body, {
        headers: {
          org: userdetails?.org,
        },
      })
      .then((res) => {
        if (res.data.length !== 0) {
          dispatch(
            setAuth({
              isLoggedIn: true,
              token: res.data.tokens.access,
              refresh: res.data.tokens.refresh,
              user: res.data.user,
              org: userdetails?.org,
            })
          );
          dispatch(
            setPanelNavigation({
              panelNavigation: res?.data?.panel_navigation,
            })
          );

          // Determine target route based on panelNavigation
          let targetRoute = '/analyst-dashboard'; // default route
          if (
            res?.data?.panel_navigation?.['manager-dashboard']?.route ===
            '/manager-dashboard'
          ) {
            targetRoute = '/manager-dashboard';
          } else if (
            res?.data?.panel_navigation?.['admin-dashboard']?.route ===
            '/admin-dashboard'
          ) {
            targetRoute = '/admin-dashboard';
          }
          history.push(targetRoute);

          if (window.apm) {
            window.apm.setUserContext({
              username: res?.data?.user?.username,
              id: res?.data?.user?.role,
              email: res?.data?.user?.email,
            });
          }

          enqueueSnackbar('Login successfully', {
            variant: 'success',
          });
          if (passwordMessage) {
            enqueueSnackbar(passwordMessage, {
              variant: 'warning',
            });
          }
        }
      })
      .catch(() => {
        // Handle errors if needed
      });
  };

  /**
   * @function refreshToken
   * @description A function for refresh Token
   * @returns {Promise} returns an axios promise
   */
  const refreshToken = (refresh) => {
    return axios
      .post(API_ENDPOINTS.REFRESH_TOKEN, {
        refresh,
        loader: false,
      })
      .then(
        (res) => {
          dispatch(
            setAuth({
              isLoggedIn: true,
              token: res.data.access,
              refresh: res.data.refresh,
              user,
              panelNavigation,
              org,
            })
          );
        },
        () => {}
      );
  };

  /**
   * @function setRefreshTokenInterval
   * @description A function to set Refresh Token Interval
   * @returns {Promise} returns an axios promise
   */
  const setRefreshTokenInterval = () => {
    const interval = setInterval(() => {
      refreshToken(localStorage.getItem('refreshToken'));
    }, 250000);
    if (refreshTokenIntervalObj) {
      clearInterval(refreshTokenIntervalObj);
    }
    setRefreshTokenIntervalObj(interval);
  };

  const logout = () => {
    apiLogout().finally(async () => {
      dispatch(resetCommonState());

      dispatch(
        setAuth({
          isLoggedIn: false,
          token: null,
          user: null,
          refresh: null,
          username: null,
          org: null,
        })
      );
      persistor.pause();
      await persistor.purge();
      sessionStorage.clear();
      localStorage.clear();
    });
  };
  const keycloakUserLogout = async () => {
    const isFromEMOT = localStorage.getItem('FROM_EMOT') === 'true';
    const redirectUri = localStorage.getItem('redirectUrl');
    try {
      if (!window.KEYCLOAK_CONFIG) {
        throw new Error('Keycloak instance not found.');
      }

      const redirectUrl = isFromEMOT
        ? redirectUri // redirect to EMOT
        : `${window.location.origin}/initial-screening/initial-my-queue`; // normal SAMS logout
      // Perform logout and wait for it to complete
      await window.KEYCLOAK_CONFIG.logout({
        redirectUri: redirectUrl,
      });

      // Clear cookies
      window?.cookieStore?.delete('RECENTLY_CLICKED_NOTIFICATION_DATA');

      //  Clear Cache Properly
      if ('caches' in window) {
        try {
          const cacheNames = await caches?.keys();
          await Promise.all(cacheNames?.map((cache) => caches.delete(cache)));
        } catch (cacheError) {
          console.error('Cache Clearing Failed:', cacheError);
        }
      }

      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      alert('Logout failed. Please try again.');
    }
  };
  /**
   * @function resetPassword
   * @description A function to reset password
   * @returns {Promise} returns an axios promise
   */
  const resetPassword = (data) => {
    return axios
      .post(
        API_ENDPOINTS.RESET_PASSWORD,
        {
          ...data,
          old_password: encrypt(data.old_password),
          new_password: encrypt(data.new_password),
          confirmPassword: encrypt(data.confirmPassword),
        },
        {
          headers: {
            org: data.accountId,
          },
        }
      )
      .then(
        () => {
          enqueueSnackbar('Password sent successfully', {
            variant: 'success',
          });
        },
        () => {}
      );
  };

  /**
   * @function changePassword
   * @description A function to change Password
   * @returns {Promise} returns an axios promise
   */
  const changePassword = (data) => {
    return axios
      .patch(
        API_ENDPOINTS.CHANGE_PASSWORD,
        {
          currentPassword: encrypt(data?.currentPassword),
          newPassword: encrypt(data?.newPassword),
          conformPassword: encrypt(data?.conformPassword),
        },
        {
          headers: {
            org: data?.accountId,
          },
        }
      )
      .then(
        (res) => {
          enqueueSnackbar(res.data.Msg, {
            variant: 'success',
          });
          setTimeout(() => {
            logout();
          }, 800);
        },
        () => {}
      );
  };
  const fetchPanelInfo = () => {
    setIsLoading(true);
    return axios.get(API_ENDPOINTS.PANEL_INFO).then(
      (res) => {
        dispatch(
          setPanelNavigation({
            panelNavigation: res?.data?.panel_navigation,
          })
        );
        return res.data;
      },
      (e) => {
        setIsLoading(false);
      }
    );
  };

  return {
    isLoggedIn,
    loginOtp,
    login,
    logout,
    resetPassword,
    changePassword,
    setRefreshTokenInterval,
    refreshTokenIntervalObj,
    user,
    isLoading,
    fetchPanelInfo,
    keycloakUserLogout,
    panelData,
    passwordError,
    email,
  };
};
export default useAuth;
